import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SimpleChartWithTableComponent } from 'app/trello/components/shared/visualisations/simple-chart-with-table/simple-chart-with-table.component';
import { ChartableData, LabelledValues } from 'app/trello/models/charts';
import { ReadableSecondsPipe } from 'app/trello/pipes/readable-seconds.pipe';
import { ChartOptions, ChartType, TooltipOptions } from 'chart.js';
import { LegendOptions } from 'chart.js/dist/types';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SimpleChartWithTableComponent,
  ],
  providers: [ReadableSecondsPipe],
  selector: 'app-pie-chart-with-table',
  template: `
      <app-simple-chart-with-table
              [chartData]="mappedChartData"
              [chartType]="chartType"
              [chartLegend]="chartLegend"
              [chartOptions]="chartOptions"></app-simple-chart-with-table>
  `,
})
export class PieChartWithTableComponent implements OnInit, OnChanges {

  @Input({required: true}) chartData!: ChartableData<LabelledValues, never>;

  @Input() limit: number | undefined;

  @Input() valueFormat: 'number' | 'human-readable-time' | 'custom' = 'number';

  @Input() valueFormatter: (value: any) => string = (value: any) => String(value);

  protected readonly chartType: ChartType = 'pie';
  protected readonly chartLegend = true;
  protected readonly chartOptions: ChartOptions<'pie'> = {
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      axis: 'xy',
      mode: 'nearest',
      intersect: true,
    },
    scales: {
      x: {
        type: 'category',
        display: false,
        title: {
          display: false,
          text: '',
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        display: false,
        title: {
          display: false,
          text: '',
        },
        ticks: {
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
      } as LegendOptions<'pie'>,
      tooltip: {
        callbacks: {
          label: (context) => {

            const label = context.dataset.label || '';
            const value = context.parsed;

            return `${label}: ${this.formatValue(value)}`;
          },
        },
      } as TooltipOptions<'pie'>,
      noTooltipColorBoxBorders: {
        enabled: true,
      },
    } as any,
  };

  protected mappedChartData!: ChartableData<LabelledValues, never>;

  constructor(
    private readonly readableSecondsPipe: ReadableSecondsPipe,
  ) {
  }

  public ngOnInit(): void {
    this.prepareData();
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartData']) {
      this.prepareData();
    }
  }

  private formatValue(
    value: any,
  ): string {

    if (this.valueFormat === 'custom') {
      return this.valueFormatter(value);
    }

    if (this.valueFormat === 'human-readable-time') {

      const seconds = Number(value) / 1000;

      return this.readableSecondsPipe.transform(seconds, 1) || '';
    }

    return String(value);
  }

  private prepareData(): void {

    if (!this.limit || this.limit <= 0) {

      this.mappedChartData = this.chartData;
      return;
    }

    const {labels, values} = this.chartData.chartData;

    const mapPrimaryColors =
      Array.isArray(this.chartData.primaryColor)
      && this.chartData.primaryColor.length === values.length;

    const mapSecondaryColors =
      Array.isArray(this.chartData.secondaryColor)
      && this.chartData.secondaryColor.length === values.length;

    const combined =
      labels.map((label, i) => ({
        label,
        value: values[i],
        primaryColor:
          mapPrimaryColors
            ? this.chartData.primaryColor![i]
            : this.chartData.primaryColor,
        secondaryColor:
          mapSecondaryColors
            ? this.chartData.secondaryColor![i]
            : this.chartData.secondaryColor,
      }));

    combined.sort((a, b) => b.value - a.value);

    const topSegments = combined.slice(0, this.limit);

    this.mappedChartData = {
      ...this.chartData,
      chartData: {
        labels: topSegments.map((item) => item.label),
        values: topSegments.map((item) => item.value),
      },
      primaryColor:
        mapPrimaryColors
          ? topSegments.map((segment) => segment.primaryColor as string)
          : this.chartData.primaryColor,
      secondaryColor:
        Array.isArray(this.chartData.secondaryColor)
          ? topSegments.map((segment) => segment.secondaryColor as string)
          : this.chartData.secondaryColor,
    };
  }

}
